/**
 * Retry Utility with Exponential Backoff
 * Retries failed async operations with configurable backoff strategy
 */

export interface RetryOptions {
  maxAttempts?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  backoffMultiplier?: number;
  jitterMs?: number;
  shouldRetry?: (error: any) => boolean;
  onRetry?: (attempt: number, error: any) => void;
}

const defaultOptions: Required<RetryOptions> = {
  maxAttempts: 3,
  initialDelayMs: 1000,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
  jitterMs: 100,
  shouldRetry: (error: any) => {
    // Retry on network errors, timeouts, and 5xx server errors
    if (
      error?.message?.includes("network") ||
      error?.message?.includes("timeout")
    ) {
      return true;
    }
    if (error?.status >= 500 && error?.status < 600) {
      return true;
    }
    // Don't retry on auth errors (401, 403) or client errors (4xx)
    if (error?.status >= 400 && error?.status < 500) {
      return false;
    }
    return true; // Retry unknown errors
  },
  onRetry: () => {},
};

/**
 * Calculate delay with exponential backoff and jitter
 */
function calculateDelay(
  attempt: number,
  initialDelay: number,
  maxDelay: number,
  multiplier: number,
  jitterMs: number,
): number {
  const exponentialDelay = initialDelay * Math.pow(multiplier, attempt - 1);
  const delayWithoutJitter = Math.min(exponentialDelay, maxDelay);
  const jitter = Math.random() * jitterMs;
  return delayWithoutJitter + jitter;
}

/**
 * Sleep for specified milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Retry an async function with exponential backoff
 *
 * @example
 * const result = await retryWithBackoff(
 *   () => fetchData(),
 *   { maxAttempts: 5, initialDelayMs: 500 }
 * );
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {},
): Promise<T> {
  const opts = { ...defaultOptions, ...options };
  let lastError: any;

  for (let attempt = 1; attempt <= opts.maxAttempts; attempt++) {
    try {
      const result = await fn();
      if (attempt > 1) {
        console.log(`[Retry] Success on attempt ${attempt}`);
      }
      return result;
    } catch (error: any) {
      lastError = error;

      // Check if we should retry this error
      if (!opts.shouldRetry(error)) {
        console.log(`[Retry] Error not retryable:`, error?.message);
        throw error;
      }

      // Don't retry if this was the last attempt
      if (attempt === opts.maxAttempts) {
        console.error(`[Retry] Max attempts (${opts.maxAttempts}) reached`);
        throw error;
      }

      const delay = calculateDelay(
        attempt,
        opts.initialDelayMs,
        opts.maxDelayMs,
        opts.backoffMultiplier,
        opts.jitterMs,
      );

      console.log(
        `[Retry] Attempt ${attempt} failed, retrying in ${Math.round(
          delay,
        )}ms...`,
        error?.message || error,
      );

      opts.onRetry(attempt, error);
      await sleep(delay);
    }
  }

  throw lastError;
}

/**
 * Create a retry wrapper for a function
 * Returns a new function that automatically retries on failure
 *
 * @example
 * const fetchWithRetry = createRetryWrapper(fetchData, { maxAttempts: 5 });
 * const result = await fetchWithRetry();
 */
export function createRetryWrapper<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  options: RetryOptions = {},
): T {
  return ((...args: any[]) => {
    return retryWithBackoff(() => fn(...args), options);
  }) as T;
}
